// INTEL CONFIDENTIAL
// Copyright (C) 2023 Intel Corporation

const learningEventsURL: any = process.env.LEARNING_EVENTS
const seekrURL: any = process.env.SEEKR_POST_URL
const predictionGuardURL: any = process.env.PREDICTION_GUARD_POST_URL
const inflectonAiURL: any = process.env.INFLECTION_AI_POST_URL

export interface ResourceAction {
  label: string
  type: 'button' | 'link' | 'links'
  variant?: string
  openInNewTab?: boolean
  redirectTo: string
  style?: any
  actions?: ResourceAction[]
}

export interface CardResource {
  isVideo: boolean
  imgSrc: string
  imgSrcSet: string
  title: string
  text?: string
  alt?: string
  actions?: ResourceAction[]
}

export interface PageResourceSection {
  title: string
  cards: CardResource[]
}

export interface PageResource {
  title: string
  text: string
  cards?: CardResource[]
  actions?: ResourceAction
  sections?: PageResourceSection[]
}

interface InitialPage {
  ai: PageResource
  rd: PageResource
  cs: PageResource
  ev: PageResource
  showCase: PageResource
  aiPosts: PageResource
}

export const initialPages: InitialPage = {
  ai: {
    title: 'Resources for your AI journey.',
    text: 'Enjoy the flexibility and control you need to innovate. From learning resources to deployment tools.',
    actions: {
      label: 'View developer solutions',
      type: 'button',
      variant: 'outline-primary',
      openInNewTab: false,
      redirectTo: '/solutions/developers'
    },
    cards: [
      {
        isVideo: true,
        imgSrc: 'https://www.youtube-nocookie.com/embed/vNkWOwE2Lsg?si=eDO2mGyytfMp8zXl',
        imgSrcSet: 'https://www.youtube-nocookie.com/embed/vNkWOwE2Lsg?si=eDO2mGyytfMp8zXl',
        alt: 'Introducing Multimodal RAG: Chat with Videos, a short course made in collaboration with deeplearningai!',
        title: 'Open source frameworks and compatible models',
        actions: []
      },
      {
        isVideo: false,
        imgSrc: '/images/events-1x.jpg',
        imgSrcSet: '/images/events-1x.jpg 1x, /images/events-2x.jpg 2x',
        title: 'Events',
        alt: 'intel greg lavender presenting on an intel developer event',
        actions: []
      },
      {
        isVideo: false,
        imgSrc: '/images/learning-1x.jpg',
        imgSrcSet: '/images/learning-1x.jpg 1x, /images/learning-2x.jpg 2x',
        title: 'Learning paths',
        alt: 'man with glasses in front of a monitor',
        actions: []
      }
    ]
  },
  rd: {
    title: 'Success stories and partnerships',
    text: "Teaming up Intel Labs' leading AI researchers, we validate the latest LLM models and work closely with enterprise customers to optimize, fine-tune, and deploy AI models in Intel Tiber AI Cloud. We partner with leading AI startups to offer trusted AI solutions powered by the latest Intel accelerated computing.",
    cards: [
      {
        isVideo: false,
        imgSrc: '/images/enterprise-1x.jpg',
        imgSrcSet: '/images/enterprise-1x.jpg 1x, /images/enterprise-2x.jpg 2x',
        title: 'Enterprise trusted AI',
        alt: 'Collaboration between seekr and intel',
        text: 'Seekr uses Intel® GPUs and Intel® Gaudi® AI processors to train and serve at scale.',
        actions: [
          {
            label: 'Seekr’s case study',
            type: 'link',
            redirectTo:
              'https://www.intel.com/content/www/us/en/developer/articles/case-study/seekr-build-trustworthy-llms-content-at-scale.html',
            openInNewTab: false
          }
        ]
      },
      {
        isVideo: false,
        imgSrc: '/images/ai-startup-accelerator-1x.jpg',
        imgSrcSet: '/images/ai-startup-accelerator-1x.jpg 1x, /images/ai-startup-accelerator-2x.jpg 2x',

        title: 'AI startup accelerator',
        alt: 'blonde developer seated in front of 2 monitors coding and her right hand on the mouse',
        text: 'Intel® Liftoff for Startups empowers AI startups with Intel’s expertise, technology, and market reach.',
        actions: [
          {
            label: 'See how startups are innovating with Intel Tiber AI Cloud',
            type: 'link',
            redirectTo: '/solutions/startups',
            openInNewTab: false
          }
        ]
      },
      {
        isVideo: false,
        imgSrc: '/images/ecosystem-and-research.jpg',
        imgSrcSet: '/images/ecosystem-and-research.jpg 1x, /images/ecosystem-and-research@2x.jpg 2x',
        title: 'Ecosystem and research',
        alt: 'developer pointed 2 hands to the screen',
        text: 'Intel Tiber AI Cloud supports code validation and upstreaming for the open-source community for AI education.',
        actions: [
          {
            label: 'Learn with our researchers',
            type: 'link',
            redirectTo:
              'https://community.intel.com/t5/Blogs/Tech-Innovation/Artificial-Intelligence-AI/Free-Multimodal-RAG-Course-from-Intel-Labs-Available-on/post/1634300',
            openInNewTab: false
          }
        ]
      }
    ]
  },
  cs: {
    title: 'Case studies',
    text: '',
    cards: [
      {
        isVideo: false,
        imgSrc: '/images/responsible-ai-card.jpg',
        imgSrcSet: '/images/responsible-ai-card.jpg',
        title: 'Seekr: Building Trustworthy LLMs for Evaluating & Generating Content ',
        alt: 'man with glasses in front of a monitor',
        actions: [{ label: 'Learn more', type: 'link', redirectTo: seekrURL }]
      },
      {
        isVideo: false,
        imgSrc: '/images/prediction-guard-logo.jpg',
        imgSrcSet: '/images/prediction-guard-logo.jpg',
        title: 'Prediction Guard De-Risks LLM Applications at Scale',
        alt: 'Prediction Guard logo',
        actions: [{ label: 'Learn   more', type: 'link', redirectTo: predictionGuardURL }]
      },
      {
        isVideo: false,
        imgSrc: '/images/inflection-ai-card.jpg',
        imgSrcSet: '/images/inflection-ai-card.jpg',
        title: 'Inflection for Enterprise, powered by Gaudi and Intel® Tiber™ AI Cloud',
        alt: 'Inflection AI logo',
        actions: [{ label: 'Learn more', type: 'link', redirectTo: inflectonAiURL }]
      }
    ]
  },
  ev: {
    title: 'Events and webinars',
    text: '',
    actions: {
      label: 'View all',
      type: 'button',
      variant: 'primary',
      openInNewTab: false,
      redirectTo: learningEventsURL
    },
    cards: [
      {
        isVideo: false,
        imgSrc: '/images/hackathon-card.jpg',
        imgSrcSet: '/images/hackathon-card.jpg',
        title: 'Hackathons',
        text: 'Showcase your Machine Learning and Deep Learning skills in our in-person or virtual events.',
        alt: 'people watching a presentation'
      },
      {
        isVideo: false,
        imgSrc: '/images/webinar-card.jpg',
        imgSrcSet: '/images/webinar-card.jpg',
        title: 'Webinars',
        text: "Get insight into what's ahead in AI engineering while making the most out of modern hardware.",
        alt: 'man attending to webinar'
      },
      {
        isVideo: false,
        imgSrc: '/images/workshop-card.jpg',
        imgSrcSet: '/images/workshop-card.jpg',
        title: 'Workshops',
        text: 'Discover the most effective techniques for creating Gen-AI apps using Intel Tiber AI Cloud.',
        alt: 'man leading a workshop'
      }
    ]
  },
  showCase: {
    title: 'Startup Showcase',
    text: 'See how startups are innovating with Intel Tiber AI Cloud',
    actions: {
      label: 'View testimonials',
      type: 'button',
      variant: 'primary',
      openInNewTab: true,
      redirectTo: 'https://community.intel.com/t5/tag/Intel%20Liftoff/tg-p/board-id/blog-ai'
    },
    cards: [
      {
        isVideo: false,
        imgSrc: '/images/startup-showcase-jamAI.jpg',
        imgSrcSet: '/images/startup-showcase-jamAI.jpg',
        title: 'Why JamAI Base Moved Embedding Models to Intel® Xeon® CPUs',
        text: 'JamAI Base optimized AI workloads for efficiency, leveraging Intel® Xeon CPUs for faster inference and better cost efficiency.',
        alt: '',
        actions: [
          {
            label: 'Learn more',
            type: 'link',
            openInNewTab: true,
            redirectTo: 'https://embeddedllm.com/blog/why-jamai-base-moved-embedding-models-to-intel-xeon-cpus'
          }
        ]
      },
      {
        isVideo: false,
        imgSrc: '/images/startup-showcase-prediction-guard.jpg',
        imgSrcSet: '/images/startup-showcase-prediction-guard.jpg',
        title: 'How Prediction Guard is Leveraging Intel® Liftoff to Scale Secure AI Solutions',
        text: 'With Intel’s cutting-edge hardware, Prediction Guard’s platform ensures high-performance AI deployment at a cost-effective price.',
        alt: '',
        actions: [
          {
            label: 'Learn more',
            type: 'link',
            openInNewTab: true,
            redirectTo:
              'https://community.intel.com/t5/Blogs/Tech-Innovation/Artificial-Intelligence-AI/How-Prediction-Guard-is-Leveraging-Intel-Liftoff-to-Scale-Secure/post/1659134'
          }
        ]
      },
      {
        isVideo: false,
        imgSrc: '/images/startup-showcase-turinTech.jpg',
        imgSrcSet: '/images/startup-showcase-turinTech.jpg',
        title: 'TurinTech AI: Driving Scalable and Sustainable AI with Intel',
        text: 'TurinTech uses Intel solutions to upgrade AI optimization. Central to this effort is the Intel® Tiber™ AI Cloud, a secure and scalable platform that accelerates the development and deployment of LLMs.',
        alt: '',
        actions: [
          {
            label: 'Learn more',
            type: 'link',
            openInNewTab: true,
            redirectTo:
              'https://community.intel.com/t5/Blogs/Tech-Innovation/Artificial-Intelligence-AI/TurinTech-AI-Driving-Scalable-and-Sustainable-AI-with-Intel/post/1644844'
          }
        ]
      }
    ]
  },
  aiPosts: {
    title: 'Resources & Events',
    text: 'Intel® Liftoff provides AI startups with the latest development tools and networking opportunities.',
    sections: [
      {
        title: 'Latest Blog Posts on AI & Cloud Computing',
        cards: [
          {
            isVideo: false,
            imgSrc: '/images/startup-event-genAI.jpg',
            imgSrcSet: '/images/startup-event-genAI.jpg',
            alt: '',
            title:
              'Finetuning & Inference on GenAI Models using Optimum Habana and the GPU Migration Toolkit on Intel®',
            actions: [
              {
                label: 'Learn more',
                type: 'link',
                redirectTo:
                  'https://community.intel.com/t5/Blogs/Tech-Innovation/Artificial-Intelligence-AI/Finetuning-Inference-on-GenAI-Models-using-Optimum-Habana-and/post/1665976',
                openInNewTab: true
              }
            ]
          },
          {
            isVideo: false,
            imgSrc: '/images/startup-event-roseman.jpg',
            imgSrcSet: '/images/startup-event-roseman.jpg',
            title: 'Roseman Labs Engine: Unveiling Breakthrough Performance with Intel® Liftoff',
            alt: '',
            actions: [
              {
                label: 'Learn more',
                type: 'link',
                redirectTo:
                  'https://community.intel.com/t5/Blogs/Tech-Innovation/Artificial-Intelligence-AI/Roseman-Labs-Engine-Unveiling-Breakthrough-Performance-with/post/1660234',
                openInNewTab: true
              }
            ]
          },
          {
            isVideo: false,
            imgSrc: '/images/startup-event-index-report.jpg',
            imgSrcSet: '/images/startup-event-index-report.jpg',
            title: 'Deciphering the AI Startup Ecosystem: Insights from the Intel® Liftoff AI Startups Index Report',
            alt: '',
            actions: [
              {
                label: 'Learn more',
                type: 'link',
                redirectTo:
                  'https://community.intel.com/t5/Blogs/Tech-Innovation/Artificial-Intelligence-AI/Deciphering-the-AI-Startup-Ecosystem-Insights-from-the-Intel/post/1656987',
                openInNewTab: true
              }
            ]
          }
        ]
      },
      {
        title: 'Past hackathons',
        cards: [
          {
            isVideo: false,
            imgSrc: '/images/startup-hackathon-third-edition.jpg',
            imgSrcSet: '/images/startup-hackathon-third-edition.jpg',
            alt: 'ai generated image of a dark data center aisle with servers onboth sides and bright led lights',
            title: 'Intel® Liftoff Days 2024 - Highlights from the Third Edition',
            actions: [
              {
                label: 'Learn more',
                type: 'link',
                redirectTo:
                  'https://community.intel.com/t5/Blogs/Tech-Innovation/Artificial-Intelligence-AI/Intel-Liftoff-Days-2024-Highlights-from-the-Third-Edition/post/1661265',
                openInNewTab: true
              }
            ]
          },
          {
            isVideo: false,
            imgSrc: '/images/startup-hackathon-multimodal-finetuning.jpg',
            imgSrcSet: '/images/startup-hackathon-multimodal-finetuning.jpg',
            title: 'Optimizing Multimodal AI Inference',
            alt: 'ai generated image of a dark data center aisle with servers onboth sides and bright led lights',
            actions: [
              {
                label: 'Learn more',
                type: 'link',
                redirectTo:
                  'https://community.intel.com/t5/Blogs/Tech-Innovation/Artificial-Intelligence-AI/Optimizing-Multimodal-AI-Inference/post/1638129',
                openInNewTab: true
              }
            ]
          },
          {
            isVideo: false,
            imgSrc: '/images/startup-hackathon-multimodal-inference.jpg',
            imgSrcSet: '/images/startup-hackathon-multimodal-inference.jpg',
            title: 'Insights from our Workshop on Inference of Multimodal Models at Intel® Liftoff Days 2024',
            alt: 'ai generated image of a dark data center aisle with servers onboth sides and bright led lights',
            actions: [
              {
                label: 'Learn more',
                type: 'link',
                redirectTo:
                  'https://community.intel.com/t5/Blogs/Tech-Innovation/Artificial-Intelligence-AI/Insights-from-our-Workshop-on-Inference-of-Multimodal-Models-at/post/1637179'
              }
            ]
          }
        ]
      }
    ]
  }
}
